export type Units = "metric" | "imperial";

const M_PER_MILE = 1609.344;
const FT_PER_M = 3.280839895;

/** Distance in km or miles (numeric), for chart axes. */
export function displayDistance(meters: number, units: Units): number {
  return units === "metric" ? meters / 1000 : meters / M_PER_MILE;
}

/** Elevation in metres or feet (numeric), for chart axes. */
export function displayElevation(meters: number, units: Units): number {
  return units === "metric" ? meters : meters * FT_PER_M;
}

export function distanceLabel(units: Units): string {
  return units === "metric" ? "km" : "mi";
}

export function elevationLabel(units: Units): string {
  return units === "metric" ? "m" : "ft";
}

export function speedLabel(units: Units): string {
  return units === "metric" ? "km/h" : "mph";
}

export function formatDistance(
  meters: number | null,
  units: Units,
  digits = 2,
): string {
  if (meters === null) return "—";
  return `${displayDistance(meters, units).toFixed(digits)} ${distanceLabel(units)}`;
}

export function formatElevation(meters: number | null, units: Units): string {
  if (meters === null) return "—";
  return `${Math.round(displayElevation(meters, units))} ${elevationLabel(units)}`;
}

export function formatSpeed(mps: number | null, units: Units): string {
  if (mps === null) return "—";
  const v = units === "metric" ? mps * 3.6 : (mps / M_PER_MILE) * 3600;
  return `${v.toFixed(1)} ${speedLabel(units)}`;
}

/** Duration as H:MM:SS (or M:SS under an hour). */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return h > 0
    ? `${h}:${mm}:${String(sec).padStart(2, "0")}`
    : `${mm}:${String(sec).padStart(2, "0")}`;
}

/** Clock time HH:MM:SS from epoch ms. */
export function formatClock(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
